import { MolsonTab, MolsonTabPanel, MolsonTabs } from '@components/reusable'
import { camelCase } from 'lodash'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

export type TabComponentProps = {
  component: React.ReactNode
  name: string
}

interface TabsProps {
  setExternalCurrentTab?: (value: number) => void
  shouldHide?: boolean
  tabsComponents: TabComponentProps[]
  isDimmed?: boolean
}

export const Tabs = ({
  setExternalCurrentTab,
  shouldHide,
  tabsComponents,
  isDimmed
}: TabsProps) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [tabValue, setTabValue] = useState<number>(0)

  const resetTabToDefault = useCallback(() => {
    setTabValue(0)
    setExternalCurrentTab?.(0)
    setSearchParams(params => {
      params.set('tab', camelCase(tabsComponents[0].name))
      return params
    })
  }, [setSearchParams, tabsComponents, setExternalCurrentTab])

  useEffect(() => {
    const currentTab = searchParams.get('tab')
    const tabIndex = tabsComponents.findIndex(
      item => camelCase(item.name) === currentTab
    )

    if (tabIndex !== -1) {
      setTabValue(tabIndex)
      setExternalCurrentTab?.(tabIndex)
    } else {
      resetTabToDefault()
    }
  }, [searchParams, resetTabToDefault, tabsComponents, setExternalCurrentTab])

  const handleChange = (event: React.SyntheticEvent) => {
    setSearchParams(params => {
      params.set('tab', camelCase(event.currentTarget.textContent ?? ''))
      return params
    })
  }

  return (
    <>
      {shouldHide ? null : (
        <MolsonTabs
          onChange={handleChange}
          value={tabValue}
          sx={{ opacity: isDimmed ? 0.5 : 1 }}
        >
          {tabsComponents.map(({ name }) => (
            <MolsonTab id={`tab_${name}`} key={name} label={name} />
          ))}
        </MolsonTabs>
      )}

      {tabsComponents.map(({ component, name }, index) => (
        <MolsonTabPanel index={index} key={name} value={tabValue}>
          {component}
        </MolsonTabPanel>
      ))}
    </>
  )
}
